import { useCallback } from 'react';
import { Lightbulb, Hammer, Rocket, BarChart3, RefreshCw } from 'lucide-react';
import { useUIStore, useDeployStore, useAiAssistantStore, useEditorStore } from '@/store';
import { cn } from '@/lib/utils';

/* ================================================================== */
/*  Lifecycle phases                                                  */
/* ================================================================== */

export type LifecyclePhase = 'idea' | 'build' | 'deploy' | 'operate' | 'improve';

interface PhaseDefinition {
  id: LifecyclePhase;
  label: string;
  icon: React.ElementType;
  panel: 'ai' | 'explorer' | 'deploy' | 'contracts' | 'ai';
  color: string;
  activeColor: string;
}

const PHASES: PhaseDefinition[] = [
  { id: 'idea', label: 'Idea', icon: Lightbulb, panel: 'ai', color: 'text-yellow-600', activeColor: 'text-yellow-400' },
  { id: 'build', label: 'Build', icon: Hammer, panel: 'explorer', color: 'text-blue-600', activeColor: 'text-blue-400' },
  { id: 'deploy', label: 'Deploy', icon: Rocket, panel: 'deploy', color: 'text-purple-600', activeColor: 'text-purple-400' },
  { id: 'operate', label: 'Operate', icon: BarChart3, panel: 'contracts', color: 'text-green-600', activeColor: 'text-green-400' },
  { id: 'improve', label: 'Improve', icon: RefreshCw, panel: 'ai', color: 'text-orange-600', activeColor: 'text-orange-400' },
];

/* ================================================================== */
/*  Infer the current lifecycle phase from app state                  */
/* ================================================================== */

export function useCurrentPhase(): LifecyclePhase {
  const { isCompiling, isDeploying, compileResult, deployResult, deployHistory } = useDeployStore();
  const { isLoading: aiLoading } = useAiAssistantStore();
  const { tabs } = useEditorStore();

  // Active deployment in progress
  if (isDeploying) return 'deploy';

  // Active compilation
  if (isCompiling) return 'deploy';

  // AI is generating → idea or improve phase
  if (aiLoading) {
    // If we already have deployed contracts, this is "improve"
    if (deployHistory.length > 0) return 'improve';
    return 'idea';
  }

  // We have a successful deploy → operate
  if (deployResult?.success || deployHistory.some((d) => d.success)) return 'operate';

  // Compile succeeded → ready to deploy
  if (compileResult?.success) return 'deploy';

  // Has files open → building
  if (tabs.length > 0) return 'build';

  // Default
  return 'idea';
}

/* ================================================================== */
/*  Component                                                         */
/* ================================================================== */

export default function LifecycleIndicator() {
  const currentPhase = useCurrentPhase();
  const { setActivePanel } = useUIStore();

  const handlePhaseClick = useCallback(
    (phase: PhaseDefinition) => {
      // Navigate to the relevant panel
      if (phase.panel === 'contracts') {
        setActivePanel('contracts' as Parameters<typeof setActivePanel>[0]);
      } else {
        setActivePanel(phase.panel as Parameters<typeof setActivePanel>[0]);
      }
    },
    [setActivePanel],
  );

  return (
    <div className="flex items-center gap-0.5" title="Developer Lifecycle">
      {PHASES.map((phase, i) => {
        const Icon = phase.icon;
        const isActive = phase.id === currentPhase;
        const phaseIndex = PHASES.findIndex((p) => p.id === currentPhase);
        const isPast = i < phaseIndex;

        return (
          <div key={phase.id} className="flex items-center">
            <button
              onClick={() => handlePhaseClick(phase)}
              className={cn(
                'flex items-center gap-0.5 px-1 py-0.5 rounded transition-all text-[9px]',
                isActive
                  ? `${phase.activeColor} bg-white/15 font-semibold`
                  : isPast
                    ? 'text-white/60 hover:text-white/80'
                    : 'text-white/30 hover:text-white/50',
              )}
              title={`${phase.label}${isActive ? ' (current)' : ''}`}
            >
              <Icon className={cn('w-2.5 h-2.5', isActive && 'drop-shadow-sm')} />
              {isActive && <span>{phase.label}</span>}
            </button>
            {/* Arrow connector */}
            {i < PHASES.length - 1 && (
              <span
                className={cn(
                  'text-[8px] mx-0.5',
                  i < phaseIndex ? 'text-white/40' : 'text-white/15',
                )}
              >
                →
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
